/**
 * C1's open roles live in Ashby (jobs.ashbyhq.com/C1). `/careers` used to only
 * embed Ashby's own job-board UI in an iframe, so every job link pointed off
 * our domain — nothing was deep-linkable or indexable on c1.ai. This module
 * fetches the same data from Ashby's public Job Board API so we can render
 * job detail pages on our own domain instead.
 *
 * Server-only: the fetch and the sanitize pipeline both run inside
 * createServerFn handlers, never in the client bundle.
 */
import { createServerFn } from "@tanstack/react-start";
import { notFound } from "@tanstack/react-router";
import { unified } from "unified";
import rehypeParse from "rehype-parse";
import rehypeSanitize, { defaultSchema } from "rehype-sanitize";
import rehypeStringify from "rehype-stringify";

// includeCompensation=true is required — Ashby omits the `compensation`
// object entirely without it, even on postings configured to display pay.
const ASHBY_JOB_BOARD_API =
  "https://api.ashbyhq.com/posting-api/job-board/C1?includeCompensation=true";

export interface AshbySalary {
  minValue?: number;
  maxValue?: number;
  currencyCode?: string;
  /** Ashby's raw interval string, e.g. "1 YEAR" or "1 HOUR". */
  interval?: string;
}

/** A structured address Ashby attaches to an office location. */
export interface AshbyAddress {
  addressLocality?: string;
  addressRegion?: string;
  addressCountry?: string;
}

/** One place a role can be based — the primary location or an alternate. */
export interface AshbyJobLocation {
  /** Display name as entered in Ashby, e.g. "Portland Office" or "Remote". */
  name: string;
  address?: AshbyAddress;
}

export interface AshbyJob {
  id: string;
  title: string;
  department?: string;
  team?: string;
  /** Primary location only. Use `locations` for display and filtering. */
  location?: string;
  /** Primary location first, then Ashby's "secondary" (alternate) locations. */
  locations: AshbyJobLocation[];
  employmentType?: string;
  workplaceType?: string;
  isRemote?: boolean;
  publishedAt?: string;
  /** Ashby's own job page — kept around as the JobPosting `url` fallback. */
  jobUrl: string;
  /** Ashby's application form — this is where every "Apply" CTA points. */
  applyUrl: string;
  /** Sanitized HTML, safe to render via dangerouslySetInnerHTML. */
  descriptionHtml: string;
  /** Full human-readable summary, e.g. "$100K – $200K • Offers Equity". Detail-page display. */
  compensationSummary?: string;
  /** Salary-only summary, e.g. "$100K - $200K". Compact list-row display. */
  salarySummary?: string;
  /** Structured salary range for schema.org JobPosting baseSalary. */
  salary?: AshbySalary;
}

interface AshbyCompensationComponent {
  compensationType: string;
  interval?: string;
  currencyCode?: string | null;
  minValue?: number | null;
  maxValue?: number | null;
}

interface AshbyCompensation {
  compensationTierSummary?: string | null;
  scrapeableCompensationSalarySummary?: string | null;
  summaryComponents?: AshbyCompensationComponent[];
}

interface AshbyApiAddress {
  postalAddress?: {
    addressLocality?: string | null;
    addressRegion?: string | null;
    addressCountry?: string | null;
  } | null;
}

interface AshbyApiSecondaryLocation {
  location?: string;
  address?: AshbyApiAddress | null;
}

interface AshbyApiJob {
  id: string;
  title: string;
  department?: string;
  team?: string;
  location?: string;
  address?: AshbyApiAddress | null;
  /** Alternate locations — separate from `location`, which is the primary only. */
  secondaryLocations?: AshbyApiSecondaryLocation[];
  employmentType?: string;
  workplaceType?: string;
  isRemote?: boolean;
  isListed?: boolean;
  publishedAt?: string;
  jobUrl: string;
  applyUrl: string;
  descriptionHtml?: string;
  /** Per-posting toggle — respect it even though we requested compensation for everyone. */
  shouldDisplayCompensationOnJobPostings?: boolean;
  compensation?: AshbyCompensation;
}

interface AshbyJobBoardResponse {
  jobs: AshbyApiJob[];
}

/** Pull the Salary component (as opposed to equity/commission) out of a compensation summary. */
function extractSalary(components?: AshbyCompensationComponent[]): AshbySalary | undefined {
  const salary = components?.find((c) => c.compensationType === "Salary");
  if (!salary || (salary.minValue == null && salary.maxValue == null)) return undefined;
  return {
    minValue: salary.minValue ?? undefined,
    maxValue: salary.maxValue ?? undefined,
    currencyCode: salary.currencyCode ?? undefined,
    interval: salary.interval ?? undefined,
  };
}

function toAddress(address?: AshbyApiAddress | null): AshbyAddress | undefined {
  const postal = address?.postalAddress;
  if (!postal) return undefined;
  const result: AshbyAddress = {
    addressLocality: postal.addressLocality ?? undefined,
    addressRegion: postal.addressRegion ?? undefined,
    addressCountry: postal.addressCountry ?? undefined,
  };
  return Object.values(result).some(Boolean) ? result : undefined;
}

/** Primary location plus alternates, de-duplicated by name, primary first. */
function collectLocations(job: AshbyApiJob): AshbyJobLocation[] {
  const candidates = [
    { name: job.location, address: job.address },
    ...(job.secondaryLocations ?? []).map((l) => ({ name: l.location, address: l.address })),
  ];
  const seen = new Set<string>();
  const locations: AshbyJobLocation[] = [];
  for (const c of candidates) {
    const name = c.name?.trim();
    if (!name || seen.has(name)) continue;
    seen.add(name);
    locations.push({ name, address: toAddress(c.address) });
  }
  return locations;
}

/**
 * Ashby's default schema is trusted enough for structural formatting, but the
 * HTML is still third-party content fetched at request time — sanitize it
 * before it ever reaches our own domain rather than trusting Ashby's editor
 * output directly.
 */
const sanitizer = unified()
  .use(rehypeParse, { fragment: true })
  .use(rehypeSanitize, defaultSchema)
  .use(rehypeStringify);

async function sanitizeDescription(html: string): Promise<string> {
  const result = await sanitizer.process(html);
  return String(result);
}

/**
 * Fetch every listed job from C1's Ashby job board.
 * Returns [] on any fetch/parse failure rather than throwing, so a transient
 * Ashby outage degrades the careers page (no open-roles list) instead of
 * breaking it.
 */
export async function fetchAshbyJobs(): Promise<AshbyJob[]> {
  try {
    const res = await fetch(ASHBY_JOB_BOARD_API, {
      headers: { Accept: "application/json" },
    });
    if (!res.ok) return [];

    const data = (await res.json()) as AshbyJobBoardResponse;
    const listed = (data.jobs ?? []).filter((j) => j.isListed !== false);

    return await Promise.all(
      listed.map(async (j): Promise<AshbyJob> => {
        // Ashby's own display toggle, per posting — some roles intentionally
        // don't publish comp even though we asked for it for everyone.
        const showComp = j.shouldDisplayCompensationOnJobPostings === true;
        return {
          id: j.id,
          title: j.title,
          department: j.department,
          team: j.team,
          location: j.location,
          locations: collectLocations(j),
          employmentType: j.employmentType,
          workplaceType: j.workplaceType,
          isRemote: j.isRemote,
          publishedAt: j.publishedAt,
          jobUrl: j.jobUrl,
          applyUrl: j.applyUrl,
          descriptionHtml: j.descriptionHtml
            ? await sanitizeDescription(j.descriptionHtml)
            : "",
          compensationSummary: showComp
            ? j.compensation?.compensationTierSummary ?? undefined
            : undefined,
          salarySummary: showComp
            ? j.compensation?.scrapeableCompensationSalarySummary ?? undefined
            : undefined,
          salary: showComp ? extractSalary(j.compensation?.summaryComponents) : undefined,
        };
      }),
    );
  } catch {
    return [];
  }
}

async function fetchAshbyJob(id: string): Promise<AshbyJob | undefined> {
  const jobs = await fetchAshbyJobs();
  return jobs.find((j) => j.id === id);
}

export const loadAshbyJobs = createServerFn({ method: "GET" }).handler(
  async () => fetchAshbyJobs(),
);

export const loadAshbyJob = createServerFn({ method: "GET" })
  .inputValidator((d: { id: string }) => d)
  .handler(async ({ data: { id } }) => {
    const job = await fetchAshbyJob(id);
    if (!job) throw notFound();
    return job;
  });
